/**
 * Lettura di un foglio Excel (.xlsx) o CSV nel browser, senza librerie.
 *
 * Perché nel browser: il file (l'export di Armis o di Medigate, l'elenco
 * dell'Ingegneria Clinica) arriva al Control Plane come intestazioni e righe
 * di testo in JSON, come ogni altra chiamata della console. Un caricamento
 * binario passerebbe dal parser multipart del WAF, che sui file binari dà
 * falsi positivi noti, e servirebbe un'eccezione apposta.
 *
 * Un .xlsx è un archivio ZIP di XML. Qui si legge la directory centrale
 * dello ZIP, si decomprimono le voci che servono con `DecompressionStream`
 * (deflate-raw, nativo nei browser moderni), e si leggono con `DOMParser` le
 * stringhe condivise e i fogli. Si tiene solo il testo delle celle: date e
 * formule non servono a un elenco di dispositivi.
 */

import { t } from './i18n/lingua'

export interface Foglio {
  nome: string
  intestazioni: string[]
  righe: (string | null)[][]
}

/** Legge un file .xlsx o .csv (o .txt/.tsv) e ne torna i fogli. */
export async function leggiFoglio(file: File): Promise<Foglio[]> {
  const nome = file.name.toLowerCase()
  if (nome.endsWith('.csv') || nome.endsWith('.txt') || nome.endsWith('.tsv')) {
    return [leggiCsv(await file.text(), file.name)]
  }
  return leggiXlsx(new Uint8Array(await file.arrayBuffer()))
}

/**
 * Il foglio con l'elenco: quello la cui prima riga ha una colonna di MAC o di
 * IP (in un export Armis è «Raw Data», non «Metadata» né «Heat Map»).
 */
export function fogliConDispositivi(fogli: Foglio[]): Foglio | null {
  const ha = (f: Foglio) => f.intestazioni.some((h) => /\b(mac|ip|ipv4)\b|indirizzo (mac|ip)/i.test(h))
  return fogli.find(ha) ?? null
}

/**
 * Il foglio come un solo testo: righe a capo, celle separate da tab, prima
 * riga le intestazioni. Tab e a capo dentro una cella diventano spazi. È la
 * forma con cui l'import viaggia verso il Control Plane (vedi
 * `api.importReferences`).
 */
export function tabellaTsv(f: Foglio): string {
  const cella = (x: string | null) => (x ?? '').replace(/[\t\r\n]+/g, ' ')
  return [f.intestazioni, ...f.righe].map((r) => r.map(cella).join('\t')).join('\n')
}

// --- CSV ---------------------------------------------------------------

/** CSV con il separatore indovinato (`,` `;` o tab) e le virgolette RFC 4180. */
export function leggiCsv(testo: string, nome = 'CSV'): Foglio {
  const pulito = testo.replace(/^﻿/, '')
  const prima = pulito.split(/\r?\n/, 1)[0] ?? ''
  const conta = (c: string) => prima.split(c).length - 1
  const sep = [',', ';', '\t'].sort((a, b) => conta(b) - conta(a))[0]
  const righe: string[][] = []
  let riga: string[] = []
  let cella = ''
  let virgolette = false
  for (let i = 0; i < pulito.length; i++) {
    const c = pulito[i]
    if (virgolette) {
      if (c === '"' && pulito[i + 1] === '"') { cella += '"'; i++ }
      else if (c === '"') virgolette = false
      else cella += c
    } else if (c === '"') virgolette = true
    else if (c === sep) { riga.push(cella); cella = '' }
    else if (c === '\n' || c === '\r') {
      if (c === '\r' && pulito[i + 1] === '\n') i++
      riga.push(cella); cella = ''
      if (riga.some((x) => x !== '')) righe.push(riga)
      riga = []
    } else cella += c
  }
  riga.push(cella)
  if (riga.some((x) => x !== '')) righe.push(riga)
  const [intestazioni = [], ...resto] = righe
  return { nome, intestazioni: intestazioni.map((h) => h.trim()), righe: resto.map((r) => r.map((x) => x || null)) }
}

// --- XLSX --------------------------------------------------------------

interface Voce { nome: string; metodo: number; compresso: number; offsetLocale: number }

/** Le voci della directory centrale dello ZIP. */
function vociZip(b: Uint8Array): Voce[] {
  const dv = new DataView(b.buffer, b.byteOffset, b.byteLength)
  // End of central directory: firma 0x06054b50, cercata dalla fine.
  let eocd = -1
  for (let i = b.length - 22; i >= Math.max(0, b.length - 65557); i--) {
    if (dv.getUint32(i, true) === 0x06054b50) { eocd = i; break }
  }
  if (eocd < 0) throw new Error(t('Non è un file Excel (.xlsx) valido: salvalo di nuovo da Excel, oppure come CSV.'))
  const n = dv.getUint16(eocd + 10, true)
  let p = dv.getUint32(eocd + 16, true)
  const voci: Voce[] = []
  const dec = new TextDecoder()
  for (let i = 0; i < n; i++) {
    if (dv.getUint32(p, true) !== 0x02014b50) break
    const metodo = dv.getUint16(p + 10, true)
    const compresso = dv.getUint32(p + 20, true)
    const lNome = dv.getUint16(p + 28, true)
    const lExtra = dv.getUint16(p + 30, true)
    const lCommento = dv.getUint16(p + 32, true)
    const offsetLocale = dv.getUint32(p + 42, true)
    voci.push({ nome: dec.decode(b.subarray(p + 46, p + 46 + lNome)), metodo, compresso, offsetLocale })
    p += 46 + lNome + lExtra + lCommento
  }
  return voci
}

async function contenuto(b: Uint8Array, v: Voce): Promise<string> {
  const dv = new DataView(b.buffer, b.byteOffset, b.byteLength)
  const lNome = dv.getUint16(v.offsetLocale + 26, true)
  const lExtra = dv.getUint16(v.offsetLocale + 28, true)
  const inizio = v.offsetLocale + 30 + lNome + lExtra
  const dati = b.slice(inizio, inizio + v.compresso)
  if (v.metodo === 0) return new TextDecoder().decode(dati)
  if (v.metodo !== 8) throw new Error(t('Compressione del file Excel non supportata ({m}): salvalo come CSV.', { m: v.metodo }))
  if (typeof DecompressionStream === 'undefined') throw new Error(t('Questo browser non sa aprire i file Excel: salva il file come CSV.'))
  const flusso = new Blob([dati]).stream().pipeThrough(new DecompressionStream('deflate-raw'))
  return new Response(flusso).text()
}

function xml(testo: string): Document {
  return new DOMParser().parseFromString(testo, 'application/xml')
}

/** «AB12» → indice di colonna 27 (0-based). */
function colonna(rif: string): number {
  const lettere = (rif.match(/^[A-Z]+/) ?? ['A'])[0]
  let n = 0
  for (const c of lettere) n = n * 26 + (c.charCodeAt(0) - 64)
  return n - 1
}

async function leggiXlsx(b: Uint8Array): Promise<Foglio[]> {
  const voci = vociZip(b)
  const per = new Map(voci.map((v) => [v.nome, v]))
  const leggi = async (nome: string) => {
    const v = per.get(nome)
    return v ? contenuto(b, v) : null
  }
  // Le stringhe condivise: le celle di testo puntano qui per indice.
  const condivise: string[] = []
  const ss = await leggi('xl/sharedStrings.xml')
  if (ss) {
    for (const si of Array.from(xml(ss).getElementsByTagName('si'))) {
      condivise.push(Array.from(si.getElementsByTagName('t')).map((x) => x.textContent ?? '').join(''))
    }
  }
  // I nomi dei fogli e il loro file, dal workbook e dalle sue relazioni.
  const wb = xml((await leggi('xl/workbook.xml')) ?? '')
  const rels = xml((await leggi('xl/_rels/workbook.xml.rels')) ?? '')
  const bersagli = new Map<string, string>()
  for (const r of Array.from(rels.getElementsByTagName('Relationship'))) {
    const target = r.getAttribute('Target') ?? ''
    bersagli.set(r.getAttribute('Id') ?? '', target.startsWith('/') ? target.slice(1) : `xl/${target}`)
  }
  const fogli: Foglio[] = []
  for (const s of Array.from(wb.getElementsByTagName('sheet'))) {
    const rid = s.getAttribute('r:id') ?? s.getAttributeNS('http://schemas.openxmlformats.org/officeDocument/2006/relationships', 'id') ?? ''
    const percorso = bersagli.get(rid)
    const testo = percorso ? await leggi(percorso) : null
    if (!testo) continue
    const righe: (string | null)[][] = []
    for (const row of Array.from(xml(testo).getElementsByTagName('row'))) {
      const riga: (string | null)[] = []
      let indice = 0
      for (const c of Array.from(row.getElementsByTagName('c'))) {
        const rif = c.getAttribute('r')
        const col = rif ? colonna(rif) : indice
        while (riga.length < col) riga.push(null)
        const tipo = c.getAttribute('t')
        let valore: string | null = null
        if (tipo === 's') valore = condivise[Number(c.getElementsByTagName('v')[0]?.textContent ?? -1)] ?? null
        else if (tipo === 'inlineStr') valore = Array.from(c.getElementsByTagName('t')).map((x) => x.textContent ?? '').join('')
        else valore = c.getElementsByTagName('v')[0]?.textContent ?? null
        riga[col] = valore
        indice = col + 1
      }
      righe.push(riga)
    }
    const [intestazioni = [], ...resto] = righe
    fogli.push({ nome: s.getAttribute('name') ?? '', intestazioni: intestazioni.map((h) => (h ?? '').trim()), righe: resto.filter((r) => r.some((x) => x != null && x !== '')) })
  }
  return fogli
}
